import { useCallback } from "react";
import { formatToLocalDateTime } from "../../system/api/util/DateAndTimeUtil";
import dayjs from "dayjs";
import { ONE_WEEK_HIDE_KEY } from "../../system/Constants";
import { Yellow100 } from "../../styles/color";

export function useMeetingRoom() {
  const findMeetingRoomInfo = useCallback((meetingRoomInfoList, name) => {
    return meetingRoomInfoList?.find((el: any) => el.displayName === name);
  }, []);

  const getIsAvailableDateRange = useCallback(
    (meetingRoomScheduleEventList, startTime, endTime) => {
      if (!meetingRoomScheduleEventList?.length) return true;

      const inputStartTime = dayjs(startTime);
      const inputEndTime = dayjs(endTime);

      const conflict = meetingRoomScheduleEventList.find((event: any) => {
        const eventStart = dayjs(formatToLocalDateTime(event.start));
        const eventEnd = dayjs(formatToLocalDateTime(event.end));

        // 시간이 겹치는지 확인: 두 범위가 겹치면 true
        const isOverlapping =
          inputStartTime.isBefore(eventEnd) && inputEndTime.isAfter(eventStart);

        return isOverlapping;
      });

      return conflict ? false : true;
    },
    []
  );

  // 가능여부 체크
  // meetingRoomEventList는 meetingRoom으로 한번 거른 리스트를 받는다.
  const getIsAvailable = useCallback((meetingRoomEventList, curTime) => {
    if (!meetingRoomEventList?.length) return true;

    const conflict = meetingRoomEventList.find((event: any) => {
      const eventStart = dayjs(formatToLocalDateTime(event.start));
      const eventEnd = dayjs(formatToLocalDateTime(event.end));

      const curInputTime = dayjs(curTime);

      // 시간이 겹치는지 확인
      const isOverlapping =
        curInputTime.isBefore(eventEnd) && curInputTime.isAfter(eventStart);

      return isOverlapping;
    });

    return conflict ? false : true;
  }, []);

  // 이름 잘라내기
  const sliceMeetingRoomName = (roomName: string): string => {
    const basementFloorRegex = /지하\s*\d+\s*층/; // ex: 지하 1층
    const floorRegex = /\d+\s*층/; // ex: 3층
    const basementOnlyRegex = /지하/; // 지하

    // 1. "지하 N층"패턴이 있으면 해당 위치 이후로 잘라냄
    const basementFloorMatch = roomName.match(basementFloorRegex);
    if (basementFloorMatch) {
      return roomName.slice(basementFloorMatch.index!);
    }

    // 2. "N층"패턴이 있으면 해당 위치 이후로 잘라냄.
    const floorMatch = roomName.match(floorRegex);
    if (floorMatch) {
      return roomName.slice(floorMatch.index!);
    }

    // 3. "지하"만 있을 경우 해당 위치 이후로 잘라냄.
    const basementOnlyMatch = roomName.match(basementOnlyRegex);
    if (basementOnlyMatch) {
      return roomName.slice(basementOnlyMatch.index!);
    }

    return roomName;
  };

  // 몇층인지 찾아내기.
  const extractFloorInfo = (roomName: string) => {
    const basementFloorMatch = roomName.match(/지하\s*(\d+)\s*층?/); // 지하2층, 지하 2층
    const hasBasement = roomName.includes("지하");
    const normalFloorMatch = roomName.match(/(\d+)\s*층/); // 2층, 10층 등

    if (basementFloorMatch) {
      const floor = parseInt(basementFloorMatch[1], 10);
      return {
        floor: -floor,
        name: `지하 ${floor}층`,
      };
    }

    if (hasBasement) {
      return {
        floor: -1,
        name: "지하 1층",
      };
    }

    if (normalFloorMatch) {
      const floor = parseInt(normalFloorMatch[1], 10);
      return {
        floor,
        name: `${floor}층`,
      };
    }

    return {
      floor: -1000, // 알 수 없음은 맨 뒤로 정렬
      name: "미분류",
    };
  };

  // 일주일간 보지 않기 값 설정
  const setHidePopupForWeek = () => {
    const oneWeekLater = dayjs().add(7, "day").toISOString();
    localStorage.setItem(ONE_WEEK_HIDE_KEY, oneWeekLater);
  };

  // 일주일간 보지 않기의 값을 삭제
  const removeHidePopupForWeek = () => {
    localStorage.removeItem(ONE_WEEK_HIDE_KEY);
  };

  // 일주일간 보지 않기 체크
  const checkShowPopup = () => {
    const storedValue = localStorage.getItem(ONE_WEEK_HIDE_KEY);

    if (!storedValue) return true; //

    const now = dayjs();
    const hideUntil = dayjs(storedValue);

    return now.isAfter(hideUntil); // 일주일 지났으면 보여주기.
  };

  // attendees에 room이 있는지 체크
  const checkRoomInAttendee = (attendees: any, room: any) => {
    // 값이 없으면 바로 false 반환
    if (!Array.isArray(attendees) || !room || !room.mail) {
      return false;
    }

    const result = attendees.some((el: any) => el?.email === room.mail);
    return result;
  };

  // attendees에 참여자인지 체크
  const checkPersonInAttendee = (attendees: any, email: any) => {
    // 값이 없으면 바로 false 반환
    if (!Array.isArray(attendees) || !email) {
      return false;
    }

    const result = attendees.some((el: any) => el?.email === email);
    return result;
  };

  // Schedular Event의 배경 색상 적용 함수
  const getMeetingRoomBgColor = (
    location: string | undefined,
    isAttendee: boolean,
    selectedMeetingRoom: any | null,
    colorList: any | undefined,
    fallbackColor: string = Yellow100 // 색상 정보가 없을 때 기본 색상
  ) => {
    let finalColor = fallbackColor;

    // location정보가 ''가 아니고, 존재하는 경우
    if (location && location.trim() !== "") {
      // ;으로 포함되어있으면 포함된 첫 번째꺼 색상을 리턴한다.
      const locations = location
        .split(";")
        .map((loc) => loc.trim())
        .filter(Boolean);

      // colorList에서 location이름이 포함된 항목의 색을 찾아 첫 번째로 매칭되는 색상 반환환
      const matchedColor = locations
        .map(
          (loc) => colorList?.find((el: any) => el.room.includes(loc))?.color
        )
        .find((color) => !!color);
      finalColor = matchedColor ?? fallbackColor;
    }

    // selectedMeetignRoom.displayName이 참석자로 있는 경우
    if (isAttendee && selectedMeetingRoom?.roomInfo?.displayName) {
      const attendeeColor = colorList?.find((el: any) =>
        el.room.includes(selectedMeetingRoom.roomInfo.displayName)
      )?.color;

      if (attendeeColor) finalColor = attendeeColor;
    }

    return finalColor;
  };

  /**
   * 내 정보 조회(my) api는 전부 소문자, organizer의 이메일은 대문자가 섞인 형태.
   * 전부 소문자인 이유는 이메일이 똑같은 글자인데, 대소문자만 바꾸는 경우 계정이 2개 생기는등 여러 이슈들로 인해 모두 소문자처리.
   * 그래서 Organizer Check로직을 전부 소문자로 바꾸어 비교하도록 수정
   */
  const checkIsOrganizer = (event: any, email: string) => {
    const result = event.organizer.email.toLowerCase() === email.toLowerCase();

    return result;
  };

  return {
    getIsAvailableDateRange,
    findMeetingRoomInfo,
    extractFloorInfo,
    sliceMeetingRoomName,
    getIsAvailable,
    setHidePopupForWeek,
    removeHidePopupForWeek,
    checkShowPopup,
    checkRoomInAttendee,
    checkPersonInAttendee,
    getMeetingRoomBgColor,
    checkIsOrganizer,
  };
}
